import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Repeat, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Auto-Reload configuration panel — for client_admin & country_operator.
 * Reads/writes /api/credit-management/auto-reload. Emits a warning at 20%
 * remaining (email + in-app), or auto-tops up when threshold + amount are set.
 */
export default function AutoReloadPanel() {
  const [cfg, setCfg] = useState({
    enabled: false, threshold_pct: 20, reload_amount: 0, monthly_cap: 0, last_triggered_at: null,
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchCfg = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/credit-management/auto-reload`);
      setCfg({
        enabled: !!r.data.enabled,
        threshold_pct: r.data.threshold_pct ?? 20,
        reload_amount: r.data.reload_amount ?? 0,
        monthly_cap: r.data.monthly_cap ?? 0,
        last_triggered_at: r.data.last_triggered_at || null,
      });
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, []);
  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const save = async (next) => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!next.enabled,
        threshold_pct: parseInt(next.threshold_pct || 20, 10),
        reload_amount: parseInt(next.reload_amount || 0, 10),
        monthly_cap: parseInt(next.monthly_cap || 0, 10),
      };
      await axios.put(`${API}/api/credit-management/auto-reload`, payload);
      setCfg(v => ({ ...v, ...payload }));
      toast.success(payload.enabled ? 'Auto-Reload enabled' : 'Auto-Reload settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save Auto-Reload');
    } finally { setSaving(false); }
  };

  if (!loaded) return null;

  return (
    <Card className="border-emerald-200 bg-emerald-50/30" data-testid="auto-reload-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Repeat className="h-4 w-4 text-emerald-600" />
          Auto-Reload Credits
          {cfg.enabled && <Badge className="bg-emerald-500 text-white text-[10px]" data-testid="auto-reload-active-badge">ACTIVE</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-1">
        <p className="text-[11px] text-gray-500 mb-3 leading-snug">
          Never run out mid-campaign. We&apos;ll automatically top up your credits when the balance falls below your threshold — and email you a receipt.
          A one-time low-balance warning is always sent at 20% remaining regardless of this setting.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="flex items-center justify-between md:justify-start md:col-span-1 gap-3 bg-white border border-gray-100 rounded-lg px-3 py-2">
            <div>
              <p className="text-[11px] font-medium text-text-primary">Enable</p>
              <p className="text-[10px] text-gray-400">Hands-free top-ups</p>
            </div>
            <Switch
              data-testid="auto-reload-enabled-switch"
              checked={cfg.enabled}
              disabled={saving}
              onCheckedChange={(v) => save({ ...cfg, enabled: v })}
            />
          </div>
          <div className="bg-white border border-gray-100 rounded-lg px-3 py-2">
            <Label className="text-[10px] text-gray-500">Trigger at (% left)</Label>
            <Input
              type="number" min="5" max="50" step="5"
              data-testid="auto-reload-threshold-input"
              className="mt-1 h-8 text-xs"
              value={cfg.threshold_pct}
              onChange={e => setCfg(v => ({ ...v, threshold_pct: parseInt(e.target.value || '20', 10) }))}
            />
          </div>
          <div className="bg-white border border-gray-100 rounded-lg px-3 py-2">
            <Label className="text-[10px] text-gray-500">Reload amount</Label>
            <Input
              type="number" min="100" step="100"
              data-testid="auto-reload-amount-input"
              className="mt-1 h-8 text-xs"
              value={cfg.reload_amount}
              onChange={e => setCfg(v => ({ ...v, reload_amount: parseInt(e.target.value || '0', 10) }))}
            />
          </div>
          <div className="bg-white border border-gray-100 rounded-lg px-3 py-2">
            <Label className="text-[10px] text-gray-500">Monthly cap (0 = unlimited)</Label>
            <Input
              type="number" min="0" step="1000"
              data-testid="auto-reload-cap-input"
              className="mt-1 h-8 text-xs"
              value={cfg.monthly_cap}
              onChange={e => setCfg(v => ({ ...v, monthly_cap: parseInt(e.target.value || '0', 10) }))}
            />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[10px] text-gray-400" data-testid="auto-reload-last-triggered">
            {cfg.last_triggered_at ? `Last reload: ${new Date(cfg.last_triggered_at).toLocaleString()}` : 'No auto-reloads triggered yet.'}
          </p>
          <Button
            size="sm" variant="outline" className="text-xs"
            disabled={saving}
            onClick={() => save(cfg)}
            data-testid="auto-reload-save-btn"
          >
            {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
